'use client';

import { useState, useEffect, useCallback } from 'react';
import { useScrollLock } from '@/lib/hooks';
import { X, Link2, Check, Mail, Download } from 'lucide-react';
import { SiTelegram, SiViber, SiLinkedin, SiFacebook } from 'react-icons/si';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { createClient } from '@/lib/supabase/client';
import { createShortLink, buildShortUrl } from '@/lib/short-links';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useToast } from '@/lib/context/ToastContext';
import { posthog } from '@/lib/analytics/posthog';
import { getCurrentUser } from '@/lib/auth/currentUser';

interface ShareSheetProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
  text?: string;
  contentId?: string;
  linkTitle?: string;
}

export default function ShareSheet({
  isOpen,
  onClose,
  url,
  title,
  text,
  contentId,
  linkTitle
}: ShareSheetProps) {
  const { t } = useLanguage();
  const toast = useToast();
  const [shareUrl, setShareUrl] = useState(url);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isQrHovered, setIsQrHovered] = useState(false);

  // Generate short link on open
  useEffect(() => {
    if (!isOpen) return;

    const generateShortLink = async () => {
      setIsLoading(true);
      try {
        const supabase = createClient();
        const user = getCurrentUser();

        if (user) {
          const { data: existingLink } = await supabase
            .from('short_links')
            .select('code')
            .eq('original_url', url)
            .eq('is_active', true)
            .single();

          if (existingLink) {
            setShareUrl(buildShortUrl(existingLink.code));
          } else {
            const { data: newLink } = await createShortLink(supabase, {
              originalUrl: url,
              title: linkTitle || title,
              contentId,
              userId: user.id
            });

            if (newLink) {
              setShareUrl(buildShortUrl(newLink.code));
            }
          }
        }
      } catch (error) {
        console.error('Error creating short link:', error);
        setShareUrl(url);
      } finally {
        setIsLoading(false);
      }
    };

    generateShortLink();
  }, [isOpen, url, title, contentId, linkTitle]);

  // Generate QR code when shareUrl is ready
  useEffect(() => {
    if (!isOpen || isLoading) return;

    QRCode.toDataURL(shareUrl, {
      width: 200,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [isOpen, isLoading, shareUrl]);

  const handleDownloadQR = useCallback(() => {
    if (!qrDataUrl) return;

    const link = document.createElement('a');
    link.download = 'qr-code.png';
    link.href = qrDataUrl;
    link.click();

    posthog?.capture('content_shared', { platform: 'qr_download', content_id: contentId });
    toast.success(t('share.downloadQR') || 'QR code downloaded');
  }, [qrDataUrl, contentId, toast, t]);

  // Centralized scroll lock
  useScrollLock(isOpen);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      toast.success(t('common.linkCopied') || 'Link copied to clipboard');
      posthog?.capture('content_shared', { platform: 'copy', content_id: contentId });
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error(t('common.copyFailed') || 'Failed to copy link');
    }
  }, [shareUrl, toast, t, contentId]);

  const trackShare = useCallback((platform: string) => {
    posthog?.capture('content_shared', {
      platform,
      content_id: contentId,
      share_url: shareUrl
    });
  }, [contentId, shareUrl]);

  // Share options with official brand icons and colors (Ukrainian market priority)
  const shareOptions = [
    {
      id: 'telegram',
      name: 'Telegram',
      icon: SiTelegram,
      bgColor: 'bg-[#26A5E4]',
      href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`
    },
    {
      id: 'viber',
      name: 'Viber',
      icon: SiViber,
      bgColor: 'bg-[#7360F2]',
      href: `viber://forward?text=${encodeURIComponent(title + '\n\n' + shareUrl)}`
    },
    {
      id: 'email',
      name: 'Email',
      icon: Mail,
      bgColor: 'bg-gray-600',
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent((text || title) + '\n\n' + shareUrl)}`
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: SiLinkedin,
      bgColor: 'bg-[#0A66C2]',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: SiFacebook,
      bgColor: 'bg-[#1877F2]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    }
  ];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-sheet-title"
    >
      <div
        className={`
          bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-3xl shadow-2xl
          transform transition-all duration-200 ease-out
          ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full sm:translate-y-4 opacity-0'}
        `}
      >
        {/* Drag handle for mobile */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 id="share-sheet-title" className="text-lg font-semibold text-gray-900">
            {t('share.title') || 'Share'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2 -m-2"
            aria-label={t('common.close') || 'Close'}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Copy Link - Primary Action */}
        <div className="px-5 py-4">
          <Button
            variant="ghost"
            onClick={handleCopyLink}
            disabled={isLoading}
            className={`
              w-full flex items-center gap-4 px-4 py-3 h-auto justify-start
              ${isCopied
                ? 'bg-green-50 ring-2 ring-green-200'
                : 'bg-gray-50 hover:bg-gray-100'
              }
            `}
          >
            <div className={`
              w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
              ${isCopied ? 'bg-green-500' : 'bg-gray-200'}
            `}>
              {isCopied ? (
                <Check className="h-6 w-6 text-white" />
              ) : (
                <Link2 className="h-6 w-6 text-gray-600" />
              )}
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="font-medium text-gray-900">
                {isCopied ? (t('common.copied') || 'Copied!') : (t('share.copyLink') || 'Copy link')}
              </div>
              {isLoading ? (
                <div className="text-sm text-gray-500">{t('share.generatingLink') || 'Generating link...'}</div>
              ) : (
                <div className="text-sm text-gray-500 truncate">{shareUrl}</div>
              )}
            </div>
          </Button>
        </div>

        {/* QR Code */}
        {qrDataUrl && (
          <div className="px-5 pb-4 flex justify-center">
            <button
              onClick={handleDownloadQR}
              onMouseEnter={() => setIsQrHovered(true)}
              onMouseLeave={() => setIsQrHovered(false)}
              className="relative group cursor-pointer rounded-xl overflow-hidden"
              aria-label={t('share.downloadQR') || 'Download QR code'}
            >
              <img
                src={qrDataUrl}
                alt={t('share.qrCode') || 'QR code'}
                width={160}
                height={160}
                className="block"
              />
              <div className={`
                absolute inset-0 bg-black/50 flex items-center justify-center
                transition-opacity duration-150
                ${isQrHovered ? 'opacity-100' : 'opacity-0'}
              `}>
                <Download className="h-8 w-8 text-white" />
              </div>
            </button>
          </div>
        )}

        {/* Share Options Grid - TikTok style */}
        <div className="px-5 pb-6">
          <div className="grid grid-cols-5 gap-2">
            {shareOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <a
                  key={option.id}
                  href={option.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare(option.id)}
                  className="flex flex-col items-center gap-2 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${option.bgColor}`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-xs text-gray-600 font-medium">{option.name}</span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Bottom safe area for mobile */}
        <div className="h-safe-area-inset-bottom pb-2 sm:pb-4" />
      </div>
    </div>
  );
}
